import { type Edge, getClosestEdge } from "@msuite/ui/dnd";
import { useQueryClient } from "@msuite/ui/query";
import { generateId } from "@msuite/utils/strings";
import type { Nullable, Tables } from "@msuite/utils/types";
import { mutators, queries, type schema } from "@msuite/zero";
import { useZero } from "@msuite/zero/react";
import type { Row } from "@rocicorp/zero";
import { useRef } from "react";
import { syncHandler } from "@/api/sync-handler";

export type TicketAssignmentType = Row<typeof schema.tables.ticketAssignments>;
export type TicketType = Tables<"tickets"> & {
	ticketFiles?: (Tables<"ticketFiles"> & {
		file: Tables<"files">;
	})[];
};
export type UserAssignmentType = Row<typeof schema.tables.userAssignments>;
export type UserType = Tables<"users">;
export type OnDropOptions = {
	edge: Nullable<Edge>;
	assignmentId: string;
};

/** Props Interface */
interface UseCrewHandlerProps {
	crewId: string;
	planId: string;
}

function useCrewHandler({ crewId, planId }: UseCrewHandlerProps) {
	const z = useZero();
	const qc = useQueryClient();

	const crewIdRef = useRef<string>(crewId);

	/** Functions */
	async function onDropTicket(ticket: TicketType, options?: OnDropOptions) {
		const crewId = getCrewId();
		const ranking = await calculateNewTicketAssignmentRanking(options);
		seedTicketCache(ticket);
		syncHandler(
			z.mutate(
				mutators.ticketAssignments.insert({
					id: generateId(),
					newReferenceId: generateId(),
					ranking,
					crewId,
					projectId: ticket.projectId,
					ticketId: ticket.id,
				}),
			),
		);
	}

	// The dropped card re-fetches the ticket over REST and renders nothing until it
	// resolves. The dragged Zero row is complete enough to paint the first frame, so
	// seed the cache with it — but never overwrite an existing entry: the drag-start
	// prefetch delivers the full record (note, assignee) and must win.
	function seedTicketCache(ticket: TicketType) {
		const queryKey = ["organization", "tickets", ticket.id];
		if (qc.getQueryData(queryKey)) return;
		qc.setQueryData(queryKey, ticket);
	}

	async function onDropTicketAssignment(assignment: TicketAssignmentType, options?: OnDropOptions) {
		const crewId = getCrewId();
		const ranking = await calculateNewTicketAssignmentRanking(options);
		if (assignment.crewId === crewId) {
			if (!options?.edge) throw new Error("Edge needed");
			if (!options?.assignmentId) throw new Error("Target needed");
			syncHandler(
				z.mutate(
					mutators.ticketAssignments.updateRanking({
						assignmentId: assignment.id,
						targetAssignmentId: options?.assignmentId,
						edge: options?.edge as "top" | "bottom",
					}),
				),
			);
		} else {
			syncHandler(
				z.mutate(
					mutators.ticketAssignments.insert({
						id: generateId(),
						newReferenceId: generateId(),
						ranking,
						crewId,
						projectId: assignment.projectId,
						ticketId: assignment.ticketId,
					}),
				),
			);
		}
	}

	async function onDeleteTicketAssignment(assignmentId: string) {
		syncHandler(z.mutate(mutators.ticketAssignments.delete({ ticketAssignmentId: assignmentId })));
	}

	async function calculateNewTicketAssignmentRanking(options?: OnDropOptions) {
		if (!options) return 0;
		const assignment = await z.run(queries.ticketAssignments.byId({ id: options.assignmentId }));
		if (!assignment) return 0;
		const targetRanking = assignment.ranking;
		if (options.edge === "bottom") return targetRanking + 1;
		if (options.edge === "top") return targetRanking - 1;
		return targetRanking;
	}

	async function onDropUser(userId: string, options?: OnDropOptions) {
		const existingAssignment = await getUserAssignmentByPlanId(userId);
		const ranking = await calculateNewUserAssignmentRanking(options);
		if (existingAssignment) {
			syncHandler(
				z.mutate(
					mutators.userAssignments.update({
						assignmentId: existingAssignment.id,
						crewId,
						ranking,
					}),
				),
			);
		} else {
			syncHandler(
				z.mutate(
					mutators.userAssignments.insert({
						id: generateId(),
						ranking,
						crewId,
						planId,
						userId,
					}),
				),
			);
		}
	}

	async function getUserAssignmentByPlanId(userId: string) {
		const assignment = await z.run(queries.userAssignments.userByPlanId({ planId, userId }));
		if (!assignment) return null;
		return assignment;
	}

	async function calculateNewUserAssignmentRanking(options?: OnDropOptions): Promise<number> {
		if (!options) return 0;
		const assignment = await z.run(queries.userAssignments.byId({ id: options.assignmentId }));
		if (!assignment) return 0;
		const targetRanking = assignment.ranking;
		if (options.edge === "bottom") return targetRanking + 1;
		if (options.edge === "top") return targetRanking - 1;
		return targetRanking;
	}

	function onDeleteCrew() {
		syncHandler(z.mutate(mutators.crews.delete({ crewId })));
	}

	function setCrewId(crewId: string) {
		crewIdRef.current = crewId;
	}

	function resetCrewId() {
		crewIdRef.current = crewId;
	}

	function getCrewId() {
		return crewIdRef.current;
	}

	return {
		onDropTicket,
		onDropTicketAssignment,
		onDropUser,
		onDeleteTicketAssignment,
		onDeleteCrew,
		getClosestEdge,
		setCrewId,
		resetCrewId,
		getCrewId,
	};
}

export { useCrewHandler };
